import { useCallback, useEffect, useImperativeHandle, useMemo, useReducer, useRef, useState, useSyncExternalStore, } from 'react';
import { Resources } from '@shared/localizations/Resources';
import { StatementStorage } from '@shared/utils/Storages/Table/StatementStorage';
import { createStatementTableColumns } from '@shared/utils/Table/Statements/StatementTableColumns';
import { StatementTableTotalService } from '@shared/utils/Table/Statements/StatementTableTotalService';
import { createContextMenuItems } from './Helpers/StatementPanelContextMenuItems';
export const useStatementPanel = (props) => {
    var _a;
    const tableRef = useRef(null);
    const [selectedRows, setSelectedRows] = useState([]);
    const [selectedRangeType, setSelectedRangeType] = useState(props.rangeType);
    const [, forceUpdate] = useReducer((x) => x + 1, 0);
    const [isShowTotals, setIsShowTotals] = useState(props.settings.isShowTotals);
    const columns = useMemo(() => createStatementTableColumns(), []);
    const [groupColumn, setGroupColumn] = useState((_a = columns.find((column) => column.id === props.settings.groupColumnId && column.isAllowGrouping)) !== null && _a !== void 0 ? _a : null);
    const [dateRange, setDateRange] = useState({ fromDate: null, toDate: null });
    const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
    const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
    const [contextMenuItems, setContextMenuItems] = useState([]);
    const storage = useMemo(() => new StatementStorage(props.statementProvider), [props.statementProvider]);
    const totalService = useMemo(() => new StatementTableTotalService(storage), [storage]);
    const snapshot = useSyncExternalStore(storage.subscribe, storage.getSnapshot);
    const onLocaleChange = useCallback(() => forceUpdate(), []);
    const handleSelectedRowsChanged = useCallback((rows) => {
        setSelectedRows(rows);
    }, []);
    const onDateRangeChange = useCallback((fromDate, toDate) => {
        setDateRange({ fromDate, toDate });
        storage.loadStatements(fromDate, toDate);
    }, [storage]);
    const onRefreshButtonClick = useCallback(() => {
        if (dateRange.fromDate && dateRange.toDate) {
            storage.loadStatements(dateRange.fromDate, dateRange.toDate);
        }
        else {
            storage.initialize();
        }
    }, [storage, dateRange]);
    const onRangeTypeChange = useCallback((type) => {
        setSelectedRangeType(type);
    }, []);
    const updateContextMenuItems = useCallback(() => {
        const items = createContextMenuItems({
            columns,
            groupByColumn: groupColumn,
            isShowTotals,
            onShowTotalsClick: () => setIsShowTotals(!isShowTotals),
            onGroupByClick: (groupByColumn) => setGroupColumn(groupByColumn),
        });
        setContextMenuItems(items);
    }, [columns, groupColumn, isShowTotals]);
    const onContextMenuOpen = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        updateContextMenuItems();
        setContextMenuPosition({ x: event.clientX, y: event.clientY });
        setIsContextMenuOpen(true);
    }, [updateContextMenuItems]);
    const onContextMenuClose = useCallback(() => setIsContextMenuOpen(false), []);
    useEffect(() => {
        Resources.subscribeOnLocaleChange(onLocaleChange);
        return () => Resources.unsubscribeOnLocaleChange(onLocaleChange);
    }, [onLocaleChange]);
    useEffect(() => {
        var _a, _b;
        (_a = tableRef.current) === null || _a === void 0 ? void 0 : _a.setIsShowTotals(isShowTotals);
        (_b = tableRef.current) === null || _b === void 0 ? void 0 : _b.setGroupColumn(groupColumn);
        if (isValidArray(contextMenuItems)) {
            updateContextMenuItems();
        }
    }, [isShowTotals, groupColumn]);
    useImperativeHandle(props.ref, () => ({
        getSettings: () => { var _a, _b; return (_b = (_a = tableRef.current) === null || _a === void 0 ? void 0 : _a.getSettings()) !== null && _b !== void 0 ? _b : {}; }
    }));
    return {
        tableRef,
        columns,
        rows: snapshot.rows,
        contextMenuPosition,
        contextMenuItems,
        isContextMenuOpen,
        selectedRangeType,
        selectedRows,
        dateRange,
        totalService,
        onRangeTypeChange,
        onDateRangeChange,
        onRefreshButtonClick,
        handleSelectedRowsChanged,
        onContextMenuOpen,
        onContextMenuClose,
        subscribeRowUpdate: storage.subscribeRowUpdate,
        subscribeTableUpdate: storage.subscribeTableUpdate,
    };
};
